import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router'
import { ConfirmActionModal } from '@/components/commons/confirm-action-modal'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ArrowRightIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { EditingQaSelectionSpaceImages } from '@/features/photoset/components/editing-qa-selection-space-images'
import { PhotosetSpaceElement } from '@/features/photoset/components/photoset-space-element'
import {
	PhotosetStepBodyContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { useCurrentPhotosetSubRoute } from '@/features/photoset/hooks/use-current-photoset-sub-route'
import { useGetPhotosetSpaces } from '@/features/photoset/queries/use-get-photoset-spaces'
import { useUpdateStepToQaReviewFlags } from '@/features/photoset/queries/use-update-step-to-qa-review-flags'
import { usePhotosetStore } from '@/features/photoset/store/store.ts'
import type { PhotosetURLParams } from '@/features/photoset/types.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const PhotosetEditingQaSelectionStep = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const currentPhotosetSubRoute = useCurrentPhotosetSubRoute()

	const {
		data: spaces,
		isFetching,
		isError
	} = useGetPhotosetSpaces({
		urlParams: {
			photosetId: urlParams.id
		},
		params: { response_type: 'extended' }
	})

	//photoset store
	const photosetSpaces = usePhotosetStore((state) => state.photosetSpaces)
	const initPhotosetSpaces = usePhotosetStore(
		(state) => state.initPhotosetSpaces
	)

	//mutations
	const {
		mutate: onUpdateStepToQaReviewFlags,
		isPending: isUpdateStepPending
	} = useUpdateStepToQaReviewFlags({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.IMAGES]
			})
		}
	})

	//statuses
	const [showConfirmationModal, setShowConfirmationModal] = useState(false)

	useEffect(() => {
		if (spaces) {
			initPhotosetSpaces(spaces)
		}
	}, [spaces, initPhotosetSpaces])

	return (
		<>
			{showConfirmationModal && (
				<ConfirmActionModal
					headerTitle={t(
						'photoset:editingQaSelection.modal.header_title'
					)}
					isLoading={isUpdateStepPending}
					onClose={() =>
						setShowConfirmationModal(!showConfirmationModal)
					}
					onConfirm={() =>
						onUpdateStepToQaReviewFlags({
							photosetId: urlParams.id
						})
					}
					paragraph={t('photoset:editingQaSelection.modal.subtitle')}
					title={t('photoset:editingQaSelection.modal.title')}
				/>
			)}
			<PageHero
				endSlot={<StepHeroControls hideSortControls />}
				subtitle={t('photoset:editingQaSelection.subtitle')}
				title={t('photoset:editingQaSelection.title')}
			/>

			<PhotosetStepWrapper>
				<PhotosetStepBodyContainer $disabled={isUpdateStepPending}>
					{isFetching &&
						[0, 1, 2].map((e) => (
							<PhotosetSpaceElement asSkeleton key={`${e}-id`} />
						))}
					{!(isFetching || isError) &&
						photosetSpaces?.map(
							(photosetSpace) =>
								photosetSpace.images.length > 0 && (
									<PhotosetSpaceElement
										endSlot={
											<EditingQaSelectionSpaceImages
												images={photosetSpace.images}
												isQaView={
													currentPhotosetSubRoute ===
													'qa'
												}
												photosetId={urlParams.id}
												spaceId={photosetSpace.id}
											/>
										}
										key={photosetSpace.id}
										spaceName={photosetSpace.space.key_name}
									/>
								)
						)}
				</PhotosetStepBodyContainer>

				{currentPhotosetSubRoute === 'qa' && (
					<PageFooter
						centerSlot={
							<p>
								{t(
									'photoset:editingQaSelection.footer_step_label'
								)}
							</p>
						}
						endSlot={
							<ButtonBrandLegacy
								disabled={isUpdateStepPending}
								onClick={() =>
									setShowConfirmationModal(
										!showConfirmationModal
									)
								}
							>
								{isUpdateStepPending && <Spinner size={20} />}
								{t('photoset:editingQaSelection.cta')}
								{!isUpdateStepPending && <ArrowRightIcon />}
							</ButtonBrandLegacy>
						}
					/>
				)}
			</PhotosetStepWrapper>
		</>
	)
}

PhotosetEditingQaSelectionStep.displayName = 'PhotosetEditingQaSelectionStep'
